import React from 'react';
import styled from 'styled-components';

const ICON_GRID = [
  { src: "/integrations/pandas-icon.svg", alt: "Pandas" },
  { src: "/integrations/tableau-icon.svg", alt: "Tableau" },
  { src: "/integrations/slack-icon.svg", alt: "Slack" },
  { src: "/integrations/sap.svg", alt: "SAP" },
  { src: "/integrations/vercel-icon.svg", alt: "Vercel" },
  { src: "/integrations/microsoft-power-bi.svg", alt: "Power BI" },
  { src: "/integrations/aws.svg", alt: "AWS" },
  { src: "/integrations/claude-icon.svg", alt: "Claude" },
  { src: "/integrations/sentry-icon.svg", alt: "Sentry" },
  { src: "/integrations/excel-icon.svg", alt: "MS Excel" },
  { src: "/integrations/atlassian.svg", alt: "Atlassian" },
  { src: "/integrations/model-context-protocol-icon.svg", alt: "MCP" },
  { src: "/integrations/numpy.svg", alt: "NumPy" },
  { src: "/integrations/pytorch-icon.svg", alt: "PyTorch" },
  { src: "/integrations/supabase-icon.svg", alt: "Supabase" },
  { src: "/integrations/deepseek-icon.svg", alt: "Deepseek" },
  { src: "/integrations/sheets-icon.svg", alt: "Google Sheets" },
  { src: "/integrations/tensorflow.svg", alt: "TensorFlow" },
  { src: "/integrations/openai-icon.svg", alt: "OpenAI" },
  { src: "/integrations/airtable.svg", alt: "Airtable" },
  { src: "/integrations/github-icon.svg", alt: "GitHub" },
  { src: "/integrations/microsoft-onedrive.svg", alt: "OneDrive" },
  { src: "/integrations/notion-icon.svg", alt: "Notion" },
  { src: "/integrations/gitlab-icon.svg", alt: "GitLab" },
];

const GRID_COLUMNS = 6;

const StyledComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 24px;
  padding-bottom: 124px;
  background: rgb(var(--whiter));
  text-align: center;

  .icon-grid {
    display: grid;
    grid-template-columns: repeat(${GRID_COLUMNS}, 1fr);
    gap: 16px;
    margin-bottom: 56px;
  }

  .icon-slot {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    background: rgba(var(--blackr), 0.04);
    border: 1px solid rgba(var(--blackr), 0.05);
    display:flex;
    align-items: center;
    justify-content: center;
  }

  .icon-slot--empty {
    background: transparent;
    border-color: transparent;
  }

  .heading {
    max-width: 720px;
    font-size: 56px;
    line-height: 1.15;
    font-weight: 400;
    color: rgba(var(--blackr), 0.85);
    margin: 0 0 24px;
  }

  .heading em {
    font-style: normal;
    color: var(--grey);
  }

  .subheading {
    max-width: 420px;
    font-size: 17px;
    line-height: 1.5;
    color: var(--grey);
    margin: 0 0 32px;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    background-color: var(--black);
    color: var(--white);
    cursor: pointer;
    transition-duration: 0.3s;

    &:hover {
      background-color: #333;
    }
  }

  .cta svg {
    padding-left: 10px;
    transition-duration: 0.3s;
  }

  .cta:hover svg {
    transform: translateX(2px);
  }

  @media (max-width: 640px) {
    padding: 64px 16px 56px;

    .icon-grid {
      gap: 10px;
      margin-bottom: 40px;
    }

    .icon-slot {
      width: 40px;
      height: 40px;
      border-radius: 12px;
    }

    .heading {
      font-size: 32px;
    }
  }
`;

// Distance-from-center fade so the grid glows brightest in the middle row
// and dims toward the outer rows/columns, matching the reference design.
const getSlotOpacity = (index: number) => {
  const row = Math.floor(index / GRID_COLUMNS);
  const col = index % GRID_COLUMNS;

  const centerRow = 1;
  const centerCol = (GRID_COLUMNS - 1) / 2;

  const rowDist = Math.abs(row - centerRow);
  const colDist = Math.abs(col - centerCol);

  const opacity = 1 - (rowDist * 0.4 + colDist * 0.13);
  return Math.max(0.12, Math.min(1, opacity));
};

const Comp = ({}) => {
  return (
    <StyledComp>
      <div className="icon-grid">
        {ICON_GRID.map((slot, index) =>
          slot === null ? (
            <div key={index} className="icon-slot icon-slot--empty" />
          ) : (
            <div
              key={index}
              className="icon-slot"
              style={{ opacity: getSlotOpacity(index) }}
            >
              <img src={slot.src} alt={slot.alt} width="20" height="20" loading="lazy" />
            </div>
          )
        )}
      </div>

      <h2 className="heading serif">
        Institutional-grade financial data for <em>developers and investors.</em>
      </h2>

      <p className="subheading">
        Integrate our financial data API into your applications, models, dashboards, and research pipelines in minutes.
      </p>
    </StyledComp>
  );
};

export default Comp;
